/**
 * src/integrations/api/auth.ts
 * Auth helpers — replaces Supabase auth.signInWithPassword / getUser / signOut
 */
import { apiPost, apiGet, tokenStore } from "./client";
import type { AppRole } from "@/lib/roles";

export type MeResult = {
  user: { id: string; email: string | null };
  profile: {
    id: string;
    full_name: string | null;
    email: string | null;
    branch_id: string | null;
    department_id: string | null;
    is_active: boolean;
  } | null;
  roles: AppRole[];
  permissions: string[];
  branches: { id: string; name: string; code: string }[];
  hod_department_ids: string[];  // raw snake_case from API
  hodDepartmentIds: string[];    // normalized camelCase alias — use this in components
  must_change_password?: boolean;
};

export async function signIn(email: string, password: string): Promise<void> {
  const data = await apiPost<{ access: string; refresh: string }>(
    "/api/auth/login/",
    { email, password }
  );
  tokenStore.set(data.access, data.refresh);
}

export async function signOut(): Promise<void> {
  const refresh = tokenStore.getRefresh();
  if (refresh) {
    try {
      await apiPost("/api/auth/logout/", { refresh });
    } catch {
      // ignore — still clear tokens
    }
  }
  tokenStore.clear();
}

import { useAuthStore as usePermStore } from "@/store/authStore";
import { useAuthStore } from "@/store/useAuthStore";

export async function getMe(): Promise<MeResult> {
  const raw = await apiGet<any>("/api/auth/me/");
  const hodIds = (raw.hod_department_ids ?? []).map(String);
  // Backend nests must_change_password inside the `user` object:
  // { user: { id, email, must_change_password }, profile, roles, ... }
  const mustChangePwd = raw.user?.must_change_password ?? raw.must_change_password ?? false;
  const me = {
    ...raw,
    hod_department_ids: hodIds,
    hodDepartmentIds: hodIds,
    must_change_password: mustChangePwd,
  };
  usePermStore.getState().setPermissions(me.permissions ?? []);
  return me;
}

export function isAuthenticated(): boolean {
  return !!tokenStore.getAccess();
}

export async function requestPasswordReset(email: string): Promise<void> {
  await apiPost("/api/auth/password-reset/", { email });
}

export async function changePassword(new_password: string, confirm_password: string): Promise<MeResult> {
  const data = await apiPost<{ access: string; refresh: string }>("/api/auth/change-password/", { new_password, confirm_password });
  if (data.access && data.refresh) {
    tokenStore.set(data.access, data.refresh);
    // Force refresh of the me profile
    const me = await getMe();
    useAuthStore.getState().setAuth(me);
    return me;
  }
  throw new Error("Failed to update password");
}
